// Un campo che si SCRIVE e insieme si SCEGLIE da un elenco.
//
// Menachem, 18/10/2026: «the neighbourhood should be able to choose by list
// but also by writing, same for cities». Il <datalist> del browser lo
// prometteva, ma su molti telefoni l'elenco non compare e sul desktop si
// apre solo dopo aver scritto. Qui l'elenco si apre col clic o con la
// freccia, si restringe mentre si scrive, e quello che si scrive resta
// valido anche se non è nell'elenco.
import React, { useMemo, useRef, useState } from 'react';
import { t } from './i18n';

const fold = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const MAX = 60;

export default function ComboBox({ value, onChange, options, placeholder, style, onBlur, autoFocus }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const boxRef = useRef(null);
  const listId = React.useId();
  const v = value || '';

  // Mentre si scrive l'elenco si restringe; aperto con la freccia su un valore
  // già scelto, mostra tutto (il valore è in cima, evidenziato).
  const [filtra, setFiltra] = useState(false);
  const visibili = useMemo(() => {
    const list = options || [];
    if (!filtra || !v.trim()) {
      const scelto = v && list.includes(v) ? [v] : [];
      return [...scelto, ...list.filter((o) => o !== v)].slice(0, MAX);
    }
    const q = fold(v);
    const inizio = list.filter((o) => fold(o).startsWith(q));
    const dentro = list.filter((o) => !fold(o).startsWith(q) && fold(o).includes(q));
    return [...inizio, ...dentro].slice(0, MAX);
  }, [options, v, filtra]);
  const altri = (options || []).length - visibili.length;

  const scegli = (o) => { onChange(o); setOpen(false); setHi(-1); setFiltra(false); };
  const tasti = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, visibili.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && open && hi >= 0 && visibili[hi]) { e.preventDefault(); scegli(visibili[hi]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        style={{ ...style, paddingRight: 30 }}
        value={v}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => { onChange(e.target.value); setFiltra(true); setOpen(true); setHi(-1); }}
        onFocus={() => { if ((options || []).length) setOpen(true); }}
        onBlur={(e) => { setTimeout(() => setOpen(false), 120); if (onBlur) onBlur(e); }}
        onKeyDown={tasti}
        role="combobox"
        aria-controls={listId}
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {(options || []).length > 0 && (
        <button type="button" tabIndex={-1} aria-label={t('Show the list')}
          onMouseDown={(e) => { e.preventDefault(); setFiltra(false); setOpen((o) => !o); }}
          style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#6b7280', padding: '4px 6px' }}>
          ▾
        </button>
      )}
      {open && visibili.length > 0 && (
        <ul role="listbox" id={listId}
          style={{ position: 'absolute', zIndex: 50, left: 0, right: 0, top: '100%', marginTop: 2, maxHeight: 240, overflowY: 'auto', background: 'white', border: '1px solid #d1d5db', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', listStyle: 'none', padding: 4 }}>
          {visibili.map((o, i) => (
            <li key={o} role="option" aria-selected={o === v}
              onMouseDown={(e) => { e.preventDefault(); scegli(o); }}
              onMouseEnter={() => setHi(i)}
              style={{ padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 14, background: i === hi ? '#eef2ff' : (o === v ? '#f3f4f6' : 'transparent'), fontWeight: o === v ? 600 : 400 }}>
              {o}
            </li>
          ))}
          {altri > 0 && (
            <li style={{ padding: '6px 8px', fontSize: 12, color: '#6b7280' }}>{t('Keep typing to narrow the list')}</li>
          )}
        </ul>
      )}
    </div>
  );
}
